import { c as _c } from "react/compiler-runtime";
import { Skeleton } from "@flat-ui";
import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { AccessLevel } from "@components/access-level/access-level";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { ProjectAddress } from "@pages/project-details/project-overview/project-address";
import { preferredAreaUnitSelector } from "@store/settings/settings-selector";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { DEFAULT_PROJECT_AREA, getProjectArea, getProjectSize } from "@utils/project-utils";
import { ChangeProjectDetailsEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";
import { UiUtils } from "@stellar/utils";
/**
 * The number of rows for the skeleton.
 * It should match the number of rows in the component, currently we have client, address, access level and area.
 */
const SKELETON_ROWS = 4;

/**
 * Shows the secondary details for a project. These include the client name, address and area.
 * They are displayed below the primary details of the project.
 */
export function SecondaryProjectDetails(t0) {
  const $ = _c(35);
  const {
    isProjectEditable,
    project,
    isLoading
  } = t0;
  const dispatch = useAppDispatch();
  const {
    canChangeProjectAccessLevel
  } = useHasUserValidPermissionProjectLevel();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    trackEvent
  } = useTrackEvent();
  const areaUnit = useAppSelector(preferredAreaUnitSelector);
  const shouldShowProjectArea = !!project && project.area?.amount !== DEFAULT_PROJECT_AREA;
  let t1;
  if ($[0] !== dispatch || $[1] !== handleErrorWithToast || $[2] !== project) {
    t1 = async function onUpdateProjectDetail(value, field) {
      if (!project) {
        return "";
      }
      ;
      try {
        await dispatch(updateProjectDetails({
          payload: {
            [field]: value
          }
        }));
        return value;
      } catch (t2) {
        const error = t2;
        handleErrorWithToast({
          id: `changeProjectDetails-${field}-${Date.now().toString()}`,
          title: `Could not change ${field}`,
          error
        });
        return project[field] ?? "";
      }
    };
    $[0] = dispatch;
    $[1] = handleErrorWithToast;
    $[2] = project;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onUpdateProjectDetail = t1;
  if (isLoading || !project) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <>{Array.from({
          length: SKELETON_ROWS
        }).map(_temp)}</>;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const t2 = project.clientName ?? "";
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      mb: "30px"
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !isProjectEditable;
  let t5;
  if ($[6] !== onUpdateProjectDetail || $[7] !== trackEvent) {
    t5 = value_0 => {
      trackEvent({
        name: ChangeProjectDetailsEvents.changeProjectClient
      });
      return onUpdateProjectDetail(value_0, "clientName");
    };
    $[6] = onUpdateProjectDetail;
    $[7] = trackEvent;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t4 || $[11] !== t5) {
    t6 = <FaroTextField label="Client" initialValue={t2} defaultEmptyValue={UiUtils.DASH} sx={t3} isReadOnly={t4} onConfirmed={t5} />;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== isProjectEditable || $[14] !== project) {
    t7 = <ProjectAddress project={project} isProjectEditable={isProjectEditable} />;
    $[13] = isProjectEditable;
    $[14] = project;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = canChangeProjectAccessLevel && isProjectEditable;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      mb: "30px"
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== project || $[18] !== t8) {
    t10 = <AccessLevel isAccessLevelEditable={t8} type="project" entity={project} location="secondary-project-details" sx={t9} />;
    $[17] = project;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== areaUnit || $[21] !== project || $[22] !== shouldShowProjectArea) {
    t11 = shouldShowProjectArea && <FaroTextField label="Area" initialValue={getProjectArea(project, areaUnit)} sx={{
      mb: "30px"
    }} />;
    $[20] = areaUnit;
    $[21] = project;
    $[22] = shouldShowProjectArea;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== project) {
    t12 = getProjectSize(project);
    $[24] = project;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      mb: "30px"
    };
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12) {
    t14 = <FaroTextField label="Size" initialValue={t12} sx={t13} />;
    $[27] = t12;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t14 || $[32] !== t6 || $[33] !== t7) {
    t15 = <>{t6}{t7}{t10}{t11}{t14}</>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t14;
    $[32] = t6;
    $[33] = t7;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
}
function _temp(_, index) {
  return <Skeleton key={index} variant="rectangular" height="48px" sx={{
    marginTop: "30px"
  }} />;
}